<template>
  <div>
    <div
      class="bg-white h-full container-with-transition"
      :style="{ minHeight: `${height + 50}px` }"
    >
      <div
        v-if="isInitialLoading"
        class="flex items-center justify-center h-[400px] bg-white"
      >
        <div class="text-center">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F95031] mx-auto mb-2" />
        </div>
      </div>

      <iframe
        v-show="!isInitialLoading"
        ref="marketplaceTransactionIframe"
        class="iframe-with-transition"
        frameborder="1"
        :style="{ height: `${height + 50}px` }"
        width="100%"
        :src="iframeSrc"
        allow="clipboard-read; clipboard-write"
        @load="handleIframeLoad"
      />

      <router-view />
    </div>

    <div
      v-if="showReadyToShipBar"
      class="app-content content !fixed h-auto !min-h-[30px] bottom-0 left-0 right-0 bg-white !px-[40px] !py-[24px] border-t-[1px] border-[#E2E2E2] flex flex-wrap justify-between items-center gap-[24px]"
    >
      <div class="flex items-center justify-start flex-wrap gap-[48px]">
        <div>
          <p class="!mb-[4px] text-[#626262]">
            Jumlah Order
          </p>
          <p class="text-[#333333] text-[24px] font-semibold">
            {{ totalOrder }}
          </p>
        </div>
        <div>
          <p class="!mb-[4px] text-[#626262]">
            Total Biaya
          </p>
          <p class="text-[#333333] text-[24px] font-semibold">
            {{ totalCost | rupiah }}
          </p>
        </div>
      </div>
      <b-button
        class="!flex items-center justify-center gap-[8px] sm:mr-[50px]"
        variant="primary"
        :disabled="isSubmitting"
        @click="handleSubmit"
      >
        <b-spinner
          v-if="isSubmitting"
          class="mr-1"
          small
        />
        {{ isSubmitting ? 'Memproses...' : 'Submit' }}
      </b-button>
    </div>

    <ModalWarehouseMappingV2
      ref="modalWarehouseMapping"
      :warehouse-fetcher="subscribedWarehouseFetcher"
      :cancel-label="thereIsUncompletedWarehousMapping ? 'Tutup' : 'Batal'"
      @success="handleSuccessMappingWarehouse"
      @cancel="handleCancelMappingWarehouse"
    >
      <template
        v-if="thereIsUncompletedWarehousMapping"
        #header
      >
        <div>
          <h4 class="text-center text-[20px] font-semibold mt-[16px] mb-[24px] text-black">
            Mapping Gudang
          </h4>
          <div class="mb-[16px]">
            <div
              v-for="(store, index) in listStoreUncompletedWM"
              :key="store.store_name"
              class="flex items-center justify-start gap-[8px] mb-[8px] last:mb-0"
            >
              <div
                class="rounded-full border-[1px] size-[22px] flex items-center justify-center"
                :class="{
                  'bg-[#F95031] text-white': index < currentShopIndex,
                  'border-[#F95031] text-[#F95031]': index === currentShopIndex,
                  'border-[#B3B3B3] text-[#B3B3B3]': index > currentShopIndex,
                }"
              >
                <span
                  v-if="index < currentShopIndex"
                  class="k-Checklist text-[20px] font-semibold"
                />
                <span v-else>
                  {{ index + 1 }}
                </span>
              </div>
              <div>
                <span class="text-[#000000]">{{ store.shop_name }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </ModalWarehouseMappingV2>

    <ModalSyncProduct
      ref="modalSyncProduct"
      :shop-id="shopId"
      :warehouse-id="warehouse.id"
      @success="handleSuccessSyncProduct"
    />

    <ModalConfirm ref="modalConfirm" />

    <ModalPackingSettings
      ref="modalPackagingSettings"
      :fetcher="fetcherPackingOptions.state.fetcher"
    />

    <ModalPacketTracking ref="modalPacketTracking" />

    <ModalLoadingDownload ref="modalLoadingDownload" />
  </div>
</template>

<script>
import { createPaginatedFetcher } from '@/@core/libs/fetch/usePaginatedFetcher'
import createRecalculation from '@/views/pages/kompack/packing/composables/useRecalculation'
import ModalPackingSettings from '../../packing/components/ModalPackingSettings.vue'
import usePackingMaterialsFetcher from '../../packing/composables/usePackingMaterialsFetcher'
import ModalConfirm from '../integrasi-marketplace/ModalConfirm.vue'
import useModalProvider from './composables/useModalProvider'
import ModalLoadingDownload from './ModalLoadingDownload.vue'
import ModalPacketTracking from './ModalPacketTracking.vue'
import ModalSyncProduct from './ModalSyncProduct.vue'
import 'vue-select/dist/vue-select.css'
import ModalWarehouseMappingV2 from '../integrasi-marketplace/ModalWarehouseMappingV2.vue'

export default {
  components: {
    ModalPackingSettings,
    ModalSyncProduct,
    ModalConfirm,
    ModalPacketTracking,
    ModalLoadingDownload,
    ModalWarehouseMappingV2,
  },
  data() {
    return {
      iframeSrc: '',
      height: 400,
      isInitialLoading: true,
      totalOrder: 0,
      totalCost: 0,
      isSubmitting: false,
      hasCheckedOrder: false,
      warehouse: {
        id: null,
      },
      subscribedWarehouseFetcher: null,
      shopId: '',
      listStoreUncompletedWM: [],
      currentShopIndex: 0,
    }
  },
  computed: {
    showReadyToShipBar() {
      return this.hasCheckedOrder
    },
    thereIsUncompletedWarehousMapping() {
      return this.listStoreUncompletedWM?.length > 0
    },
  },
  created() {
    const query = new URLSearchParams(this.$route.query).toString()
    this.iframeSrc = `${process.env.VUE_APP_BASE_AUTH_MODULE}/kompack/marketplace-transaction${query ? `?${query}` : ''}`
    this.initializeComponent()
    this.initializeFetcherWarehouses()
  },
  mounted() {
    this.modalProvider = useModalProvider(this)
    window.addEventListener('message', this.watchIframe)
  },
  destroyed() {
    window.removeEventListener('message', this.watchIframe)
  },
  methods: {
    /**
       * Handle the iframe message event
       * @param {Object} event - the iframe message event
       */
    async watchIframe(event) {
      if (event.origin !== process.env.VUE_APP_BASE_AUTH_MODULE) return

      const {
        type,
        data,
      } = event.data

      if (type === 'UNCOMPLETED_WAREHOUSE_MAPPING') {
        const { stores } = data
        this.listStoreUncompletedWM = stores
        this.currentShopIndex = 0
        await this.$nextTick()
        this.$refs.modalWarehouseMapping.show(stores[0])
        return
      }

      if (type === 'WAREHOUSE_MAPPING') {
        this.$refs.modalWarehouseMapping.show(data)
        return
      }

      if (type === 'SUCCESS_MAPPING_WAREHOUSE') {
        this.$refs.modalWarehouseMapping.hide()
        return
      }

      if (type === 'ERROR_UPDATE_WAREHOUSE') {
        this.$toast_error({
          message: 'Gagal update warehouse, silahkan coba lagi',
        })
        return
      }

      if (type === 'SUCCESS_REFRESH_ORDER') {
        this.$refs.modalLoadingDownload.closeModal()
        return
      }

      if (type === 'REFRESH_ORDER') {
        this.$refs.modalLoadingDownload.showModal()
        return
      }

      if (type === 'ERROR_REFRESH_ORDER') {
        this.$toast_error({
          message: 'Gagal refresh order, silahkan coba lagi',
        })
        this.$refs.modalLoadingDownload.closeModal()
        return
      }

      if (type === 'TRACK_PACKAGE') {
        this.handleShowModalPacketTracking(data)
        return
      }

      if (type === 'GO_BACK') {
        if (window.history.length > 2) {
          this.$router.go(-1)
          return
        }

        this.$router.push({ name: 'search-gudang' })
        return
      }

      if (type === 'GO_TO' && data.destination === 'mi:pm') {
        this.$router.push({
          name: 'integrasi-marketplace-mapping-product',
          params: { shopId: data.shop_id },
          query: { marketplace: data.marketplace },
        })

        return
      }

      if (type === 'GO_TO' && data.destination === 'mi:vm') {
        this.$router.push({
          name: 'integrasi-marketplace-mapping-variants',
          params: { shopId: data.shop_id, productId: data.marketplace_product_id },
          query: { marketplace: data.marketplace },
        })

        return
      }

      // Handle the process of setting the packing materials the selected order
      if (type === 'SET_PACKING') {
        if (this.warehouse.id !== data.warehouse_id) {
          this.warehouse.id = data.warehouse_id
          this.fetcherPackingOptions.state.fetcher.fetchData({ reset: true, options: { warehouseId: data.warehouse_id } })
        }
        const payload = {
          products: data.products?.map(item => ({
            product_id: item.product_id,
            total_item: item.quantity,
            is_product_bonus: item.is_product_bonus,
            is_bundling: item?.is_bundling,
            storage_type: item.storage_type,
            bundle_components: (item?.product_bundles ?? item?.bundle_components ?? []).map(component => ({
              product_id: component.product_id,
              qty: component.quantity,
              is_product_bonus: component.is_product_bonus,
              storage_type: component.storage_type,
            })),
          })),
          packings: data.packing_materials || [],
          warehouse_id: data.warehouse_id,
          total_order: data.products.reduce((acc, item) => acc + item.quantity, 0),
        }

        this.$refs.modalPackagingSettings.show({
          position: data.id,
          packings: data.packing_materials || [],
          beforeSave: async dataPayload => {
            const fulfillmentCost = await this.recalculation.recalculateFulfillment({
              ...payload,
              packings: dataPayload.data.map(item => ({
                id: item.id,
                total_layer: Number(item.layer),
              })),
            }, true)
            this.$refs.marketplaceTransactionIframe.contentWindow.postMessage({
              type: 'SUCCESS_SAVE_PACKING',
              data: {
                id: data.id,
                packing_materials: dataPayload.data || data.packing_materials,
                cost: {
                  ...fulfillmentCost.data,
                  fulfillment_fee: fulfillmentCost.data.fulfillment_cost,
                },
              },
            }, '*')
          },
        })
        return
      }

      // Dynamically sync the product and variant
      if (type === 'SYNC_PRODUCT') {
        if (!data.product_id || !data.variant_id) {
          this.warehouse.id = data.warehouse_id
          this.shopId = data.shop_id
          this.$refs.modalSyncProduct.showModal({
            type: (data.product_id && !data.variant_id) ? 'variant' : 'product',
            data,
          })
        }

        return
      }

      if (type === 'SUCCESS_SUBMIT_MARKETPLACE_ORDER') {
        this.modalProvider.transactionSuccess()
        this.isSubmitting = false
        return
      }

      if (type === 'ERROR_SUBMIT_MARKETPLACE_ORDER') {
        if (data.message === 'fund not enough') {
          this.modalProvider.fundNotEnough()
        }
        if (data.message === 'stock not enough') {
          this.modalProvider.stockNotEnough(data.stockList)
        }

        if (data.message === 'not defined error') {
          this.modalProvider.notDefinedError()
        }
        this.isSubmitting = false
        return
      }

      if (type === 'ERROR_SYNC_WAREHOUSE') {
        this.$toast_error({
          message: 'Gagal assign warehouse, silahkan coba lagi',
        })

        return
      }

      if (type === 'TOGGLE_READY_TO_SHIP_BAR') {
        const { status, fulfillment_fee, total_order } = data

        this.totalCost = fulfillment_fee
        this.totalOrder = total_order
        this.hasCheckedOrder = status === 'has-checked-order'
        return
      }

      if (type === 'SYNC_FILTER') {
        const currentQuery = { ...this.$route.query, ...data.query }

        this.$router.replace({
          name: this.$route.name,
          params: this.$route.params,
          query: currentQuery,
        })
      }

      if (type === 'SET_DIMENSION' && data.height > 400) {
        this.height = Math.floor(data.height)
      }
    },
    /**
       * Handle the iframe load event
       */
    handleIframeLoad() {
      this.isInitialLoading = false
    },
    /**
       * Handle routing user back to the previous page
       */
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.$router.push({ name: 'search-gudang' })
      }
    },
    handleSubmit() {
      if (this.isSubmitting) return
      this.isSubmitting = true
      this.$refs.marketplaceTransactionIframe.contentWindow.postMessage({
        type: 'SUBMIT_MARKETPLACE_ORDER',
      }, '*')
    },
    initializeComponent() {
      this.fetcherPackingOptions = usePackingMaterialsFetcher(this)
      this.fetcherPackingOptions.initializeFetcherPackingMaterials()
      this.recalculation = createRecalculation(this)
    },
    handleSuccessSyncProduct(data) {
      this.$refs.marketplaceTransactionIframe.contentWindow.postMessage({
        type: 'SUCCESS_SYNC_PRODUCT',
        data,
      }, '*')

      this.$refs.modalSyncProduct.hideModal()
    },
    handleShowModalPacketTracking(data) {
      this.$refs.modalPacketTracking.showModal(data)
    },
    initializeFetcherWarehouses() {
      const fnFetch = ({
        offset, limit,
      }) => this.$http_new.get('/komship/api/v1/subscribe-warehouses/products', {
        params: {
          offset,
          limit,
        },
      }).then(res => {
        const { data } = res.data
        if (!data) {
          return {
            data: [],
          }
        }

        return {
          data,
        }
      }).catch(() => {
        this.$toast_error({
          message: 'Gagal mengambil data gudang, silahkan coba lagi',
        })

        throw new Error('Gagal mengambil data gudang, silahkan coba lagi')
      })

      this.subscribedWarehouseFetcher = createPaginatedFetcher(fnFetch, 0, 50)
    },
    handleSuccessMappingWarehouse(results) {
      // if there is still uncompleted warehouse mapping, show the next one
      if (this.listStoreUncompletedWM.length > 0 && this.currentShopIndex < this.listStoreUncompletedWM.length - 1) {
        this.currentShopIndex += 1
        this.$refs.modalWarehouseMapping.show(this.listStoreUncompletedWM[this.currentShopIndex])
        return
      }
      this.$refs.marketplaceTransactionIframe.contentWindow.postMessage({
        type: 'SUCCESS_MAPPING_WAREHOUSE',
        data: { results },
      }, '*')
    },
    handleCancelMappingWarehouse() {
      // if mapping warehouse cancelled but there is completed mapping warehouse before,
      // tell to iframe to treat as success_mapping_warehouse
      if (this.listStoreUncompletedWM.length > 0 && this.currentShopIndex > 0) {
        this.$refs.marketplaceTransactionIframe.contentWindow.postMessage({
          type: 'SUCCESS_MAPPING_WAREHOUSE',
        }, '*')
      }
    },
  },
}
</script>

<style scoped>
.container-with-transition {
  transition: min-height 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.iframe-with-transition {
  transition: height 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

@media (prefers-reduced-motion: reduce) {

  .container-with-transition,
  .iframe-with-transition {
    transition: none;
  }
}
</style>
